import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { Typography } from '@mui/material';
import { useUserData } from 'hooks/use-user-data';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { Roles, Routes } from '../enums';

interface AdminPage {
  key: string;
  name: string;
  purpose: string;
  icon: React.ReactNode;
  route: Routes;
}

const Administration: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useUserData();

  const pages: AdminPage[] = [
    {
      key: 'prompts',
      name: t('prompts'),
      purpose: t('adminPurposePrompts'),
      icon: <CommentOutlinedIcon />,
      route: Routes.PROMPT_MANAGEMENT,
    },
    {
      key: 'statusMessages',
      name: t('statusMessages'),
      purpose: t('adminPurposeStatusMessages'),
      icon: <InfoOutlinedIcon />,
      route: Routes.STATUS_MESSAGE_MANAGEMENT,
    },
    {
      key: 'monitoring',
      name: t('adminNavMonitoring'),
      purpose: t('adminPurposeMonitoring'),
      icon: <ShowChartOutlinedIcon />,
      route: Routes.ADMIN_MONITORING,
    },
    {
      key: 'userOverview',
      name: t('adminNavUserOverview'),
      purpose: t('adminPurposeUserOverview'),
      icon: <GroupOutlinedIcon />,
      route: Routes.ADMIN_USER_OVERVIEW,
    },
    {
      key: 'queue',
      name: t('adminNavQueue'),
      purpose: t('adminPurposeQueue'),
      icon: <FormatListBulletedOutlinedIcon />,
      route: Routes.ADMIN_QUEUE_MANAGEMENT,
    },
    {
      key: 'campaignTimeline',
      name: t('adminNavCampaignTimeline'),
      purpose: t('adminPurposeCampaignTimeline'),
      icon: <TimelineOutlinedIcon />,
      route: Routes.ADMIN_CAMPAIGN_CONTACT_TIMELINE,
    },
    {
      key: 'linkedinSession',
      name: t('adminNavLinkedinSession24h'),
      purpose: t('adminPurposeLinkedinSession'),
      icon: <AccessTimeOutlinedIcon />,
      route: Routes.ADMIN_LINKEDIN_SESSION_MONITOR,
    },
    {
      key: 'feedbackTickets',
      name: t('adminNavFeedbackTickets'),
      purpose: t('adminPurposeFeedbackTickets'),
      icon: <FeedbackOutlinedIcon />,
      route: Routes.ADMIN_FEEDBACK_TICKETS,
    },
    {
      key: 'engagement',
      name: t('adminNavEngagement'),
      purpose: t('adminPurposeEngagement'),
      icon: <InsightsOutlinedIcon />,
      route: Routes.ADMIN_ENGAGEMENT,
    },
  ];

  if (role !== Roles.OWNER) {
    return (
      <div className={classes.administration}>
        <Typography>{t('noPermission', 'You do not have permission to access this page.')}</Typography>
      </div>
    );
  }

  return (
    <div className={classes.administration}>
      <div className={classes.pageHeader}>
        <Typography className={classes.pageTitle}>{t('administration')}</Typography>
        <Typography className={classes.pageSubtitle}>{t('adminOverviewSubtitle')}</Typography>
      </div>
      <div className={classes.grid}>
        {pages.map((page) => (
          <div
            key={page.key}
            className={classes.card}
            role={'button'}
            tabIndex={0}
            onClick={() => navigate(page.route)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navigate(page.route);
              }
            }}
          >
            <span className={classes.iconTile}>{page.icon}</span>
            <span className={classes.cardText}>
              <Typography className={classes.cardName}>{page.name}</Typography>
              <Typography className={classes.cardPurpose}>{page.purpose}</Typography>
            </span>
            <ChevronRightIcon className={classes.chevron} />
          </div>
        ))}
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  administration: {
    padding: '32px 48px 48px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    [theme.breakpoints.down('lg')]: {
      padding: '20px 16px 24px 16px',
    },
  },
  pageHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  pageTitle: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    letterSpacing: '-0.2px',
    color: theme.palette.text.primary,
  },
  pageSubtitle: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 16,
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  card: {
    background: '#FFF',
    borderRadius: 12,
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',
    transition: 'box-shadow 120ms ease-in-out',
    outline: 'none',
    '&:hover, &:focus-visible': {
      boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.12)',
    },
  },
  iconTile: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: theme.palette.backgrounds.main,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
      fontSize: 22,
    },
  },
  cardText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  },
  cardName: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  cardPurpose: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  chevron: {
    color: theme.palette.greys.light,
    fontSize: 20,
    flexShrink: 0,
  },
}));

export default Administration;
